import { useState, type ReactNode, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { DataGridV2RowAction, DataGridV2Tone } from './types';

/**
 * Palette extracted from the Bloomays CRA mockups. Kept local to the V2 grid so
 * the component renders identically to the maquettes without depending on the
 * exact shade names of the shared theme.
 */
export const TONES: Record<DataGridV2Tone, { color: string; bg: string }> = {
  neutral: { color: '#6B6B7E', bg: '#F2F2F5' },
  info: { color: '#1449B8', bg: '#EDF3FE' },
  success: { color: '#4D7236', bg: '#EEF4EB' },
  warning: { color: '#C17A00', bg: '#FDFAF0' },
  danger: { color: '#FF3328', bg: '#FFF0EF' },
  purple: { color: '#5558D4', bg: '#F0F0FE' },
};

const AVATAR_TONES: DataGridV2Tone[] = ['info', 'success', 'danger', 'purple', 'warning'];

/** Deterministically pick an avatar tone from a seed (e.g. the initials). */
export const avatarToneFromSeed = (seed: string): DataGridV2Tone => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[hash % AVATAR_TONES.length];
};

const euroFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

export const formatEuro = (amount?: number | null): string =>
  amount === null || amount === undefined ? '—' : euroFormatter.format(amount);

/* ── Status chip / signature pill ───────────────────────────── */

export type StatusChipProps = {
  tone?: DataGridV2Tone;
  dot?: boolean;
  children: ReactNode;
};

export const StatusChip = ({ tone = 'neutral', dot = false, children }: StatusChipProps) => (
  <ChipRoot tone={tone}>
    {dot ? <ChipDot tone={tone} /> : null}
    {children}
  </ChipRoot>
);

/* ── Avatar cell (initials + name + sub-label) ──────────────── */

export type AvatarCellProps = {
  initials: string;
  name?: ReactNode;
  subtitle?: ReactNode;
  tone?: DataGridV2Tone;
};

export const AvatarCell = ({ initials, name, subtitle, tone }: AvatarCellProps) => (
  <Stack direction="row" alignItems="center" spacing={1.1}>
    <AvatarCircle tone={tone ?? avatarToneFromSeed(initials)}>{initials}</AvatarCircle>
    {(name || subtitle) && (
      <Box sx={{ minWidth: 0 }}>
        {name ? <AvatarName>{name}</AvatarName> : null}
        {subtitle ? <AvatarSubtitle>{subtitle}</AvatarSubtitle> : null}
      </Box>
    )}
  </Stack>
);

/* ── Progress cell ──────────────────────────────────────────── */

export type ProgressCellProps = {
  value: number;
  max: number;
  label?: ReactNode;
};

export const ProgressCell = ({ value, max, label }: ProgressCellProps) => {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const tone: DataGridV2Tone = pct >= 100 ? 'success' : pct > 0 ? 'info' : 'neutral';
  return (
    <ProgressRoot>
      <ProgressTrack>
        <ProgressFill pct={pct} tone={tone} />
      </ProgressTrack>
      <ProgressLabel tone={tone}>{label ?? `${value}/${max}`}</ProgressLabel>
    </ProgressRoot>
  );
};

/* ── Money cell ─────────────────────────────────────────────── */

export const MoneyCell = ({ amount }: { amount?: number | null }) => <MoneyText>{formatEuro(amount)}</MoneyText>;

/* ── Row actions dropdown ───────────────────────────────────── */

export type RowActionsMenuProps = {
  actions: DataGridV2RowAction[];
  label?: ReactNode;
};

export const RowActionsMenu = ({ actions, label }: RowActionsMenuProps) => {
  const { t } = useTranslation(['dataGrid']);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event?: object) => {
    (event as MouseEvent | undefined)?.stopPropagation?.();
    setAnchorEl(null);
  };

  if (!actions.length) return null;

  return (
    <>
      <ActionTrigger
        type="button"
        onClick={handleOpen}
        aria-label={t('actions')}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {label ?? t('actions')}
        {label == null ? <KeyboardArrowDownIcon sx={{ fontSize: 14, opacity: 0.5 }} /> : null}
      </ActionTrigger>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // self-contained styling: defeat any host-theme `MuiMenu` overrides (the
        // shared theme forces every menu to width 210 / list padding 0) so the
        // action menu looks the same in every app, like the story.
        slotProps={{
          paper: {
            sx: {
              width: 'auto',
              minWidth: 240,
              maxWidth: 360,
              borderRadius: 2.5,
              mt: 0.5,
              boxShadow: '0 8px 28px rgba(33,33,33,.16)',
            },
          },
          list: { sx: { py: 0.75 } },
        }}
      >
        {actions.map((action, index) => [
          action.divider && index > 0 ? <Divider key={`divider-${index}`} sx={{ my: 0.5 }} /> : null,
          <MenuItem
            key={index}
            disabled={action.disabled}
            onClick={(event) => {
              event.stopPropagation();
              action.onClick();
              handleClose();
            }}
            sx={{
              minHeight: 'auto',
              py: 1,
              px: 1.5,
              mx: 0.75,
              borderRadius: 1.5,
              gap: 1.25,
              fontSize: 13,
              fontWeight: 500,
              lineHeight: 1.4,
              color: action.danger ? TONES.danger.color : undefined,
            }}
          >
            {action.icon ? (
              <ListItemIcon sx={{ minWidth: 'auto !important', mr: 0, color: 'inherit', '& svg': { fontSize: 18 } }}>
                {action.icon}
              </ListItemIcon>
            ) : null}
            {action.label}
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
};

/* ── styles ─────────────────────────────────────────────────── */

const ChipRoot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: DataGridV2Tone }>(({ tone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 10px',
  borderRadius: 20,
  fontSize: 11.5,
  fontWeight: 600,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  color: TONES[tone].color,
  backgroundColor: TONES[tone].bg,
}));

const ChipDot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: DataGridV2Tone }>(({ tone }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: TONES[tone].color,
}));

const AvatarCircle = styled('div', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: DataGridV2Tone }>(({ tone }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 11.5,
  fontWeight: 700,
  flexShrink: 0,
  letterSpacing: '-0.02em',
  color: TONES[tone].color,
  backgroundColor: TONES[tone].bg,
}));

const AvatarName = styled('div')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.3,
  color: theme.palette.neutral?.[90] ?? '#212121',
  whiteSpace: 'nowrap',
}));

const AvatarSubtitle = styled('div')(() => ({
  fontSize: 11,
  lineHeight: 1.3,
  color: '#9898A8',
  whiteSpace: 'nowrap',
}));

const ProgressRoot = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 96,
  width: '100%',
}));

const ProgressTrack = styled('div')(() => ({
  flex: 1,
  height: 5,
  borderRadius: 3,
  backgroundColor: '#E4E4EA',
  overflow: 'hidden',
}));

const ProgressFill = styled('div', {
  shouldForwardProp: (prop) => prop !== 'pct' && prop !== 'tone',
})<{ pct: number; tone: DataGridV2Tone }>(({ pct, tone }) => ({
  height: '100%',
  borderRadius: 3,
  width: `${pct}%`,
  backgroundColor: TONES[tone].color,
  transition: 'width .6s cubic-bezier(.22,.61,.36,1)',
}));

const ProgressLabel = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: DataGridV2Tone }>(({ tone }) => ({
  fontSize: 11,
  fontWeight: 700,
  minWidth: 38,
  textAlign: 'right',
  whiteSpace: 'nowrap',
  color: TONES[tone].color,
}));

const MoneyText = styled('span')(() => ({
  fontSize: 13,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  color: '#4D7236',
}));

const ActionTrigger = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '5px 12px',
  borderRadius: 8,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.neutral?.[90] ?? '#212121',
  border: `1px solid ${theme.palette.neutral?.[20] ?? '#E4E4EA'}`,
  background: theme.palette.neutral?.white ?? '#fff',
  transition: 'all .15s',
  '&:hover': {
    background: '#F9F9FB',
    borderColor: '#C8C8D4',
  },
}));
